import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ScenarioCard from './ScenarioCard';
import styles from './styles/ScenarioCarousel.module.css';
import Loading from '../../loading/Loading';

const ITEMS_PER_PAGE = 20;

const ScenarioCarousel = ({ activeTab, activeSubTab, mapSubTabToTag }) => {
    const [scenariosList, setScenariosList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);

    const fetchScenarios = useCallback(async (currentPage, isNewTab) => {
        if (isNewTab) {
            setIsLoading(true);
            setScenariosList([]);
            setError(null);
        } else {
            setIsLoadingMore(true);
        }

        let filterType = '';
        let tag = '';

        const tabKey = activeTab.toLowerCase().replace(/\s+/g, '');
        switch (tabKey) {
            case 'featured':
                filterType = 'featured';
                break;
            case 'mostviewed':
                filterType = 'mostViewed';
                break;
            case 'new':
                filterType = 'new';
                break;
            case 'recentlyupdated':
                filterType = 'recentlyUpdated';
                break;
            case 'education':
                filterType = 'tag';
                tag = activeSubTab ? mapSubTabToTag(activeSubTab) : 'education';
                break;
            case 'entertainment':
                filterType = 'tag';
                tag = activeSubTab ? mapSubTabToTag(activeSubTab) : 'entertainment';
                break;
            default:
                console.warn('Unknown tab:', activeTab);
                filterType = 'featured';
        }
        
        const params = {
            filterType,
            page: currentPage,
            limit: ITEMS_PER_PAGE,
        };

        if (tag) {
            params.tag = tag;
        }

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/homepage`, { params });
            const { scenarios, hasMore: newHasMore } = response.data;

            if (isNewTab) {
                setScenariosList(scenarios);
            } else {
                setScenariosList(prevScenarios => [...prevScenarios, ...scenarios]);
            }
            setHasMore(newHasMore);
            setPage(currentPage);

        } catch (err) {
            console.error(`Error fetching ${filterType} scenarios:`, err);
            setError(`Failed to load scenarios. ${err.response?.data?.message || err.message}`);
            setScenariosList([]);
        } finally {
            if (isNewTab) setIsLoading(false);
            else setIsLoadingMore(false);
        }
    }, [activeTab, activeSubTab, mapSubTabToTag]);

    useEffect(() => {
        setPage(1);
        fetchScenarios(1, true);
    }, [activeTab, activeSubTab, fetchScenarios]);

    const handleLoadMore = () => {
        if (!isLoadingMore && hasMore) {
            fetchScenarios(page + 1, false);
        }
    };

    if (isLoading) {
        return <div className={styles.centeredContainer}><Loading /></div>;
    }

    if (error) {
        return <div className={styles.errorState}>Error: {error}</div>;
    }

    if (scenariosList.length === 0 && !isLoading) {
        return <div className={styles.emptyState}>No scenarios found for this category.</div>;
    }

    return (
        <>
            <div className={styles.gridContainer}>
                {scenariosList.map((scenario) => (
                    <ScenarioCard key={scenario._id} scenario={scenario} />
                ))}
            </div>
            {isLoadingMore && (
                <div className={styles.loadingIndicatorContainer}>
                    <Loading small />
                </div>
            )}
            {hasMore && !isLoadingMore && (
                <div className={styles.loadMoreActionContainer}>
                    <button onClick={handleLoadMore} className={styles.loadMoreButton}>
                        Load More Scenarios
                    </button>
                </div>
            )}
        </>
    );
};

export default ScenarioCarousel;